import { motion } from "framer-motion";
import { Shield, Lock, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import GlobalHeader from "@/components/GlobalHeader";

export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      {/* Global Header */}
      <GlobalHeader />

      <div className="container mx-auto px-4 py-12 max-w-4xl pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <Shield className="w-16 h-16 mx-auto text-primary" />
            <h1 className="text-4xl md:text-5xl font-bold">Datenschutzerklärung</h1>
            <p className="text-muted-foreground">
              Ihre Privatsphäre ist uns wichtig
            </p>
          </div>

          {/* Content */}
          <div className="bg-card rounded-2xl p-8 border shadow-sm space-y-8">
            {/* Section 1 */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-bold">1. Datenschutz auf einen Blick</h2>
              </div>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Section 2 */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">2. Datenerfassung auf unserer Website</h3>
              <div className="text-muted-foreground space-y-3">
                <p>
                  <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
                </p>
                <p>
                  <strong>Wie erfassen wir Ihre Daten?</strong><br />
                  Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
                </p>
                <p>
                  Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Section 3 */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">3. Hosting</h3>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Wir hosten die Inhalte unserer Website bei folgenden Anbietern:<br />
                  <strong>Google Cloud EMEA Limited (Backend), Google Ireland Limited (Frontend Hosting) und MongoDB Limited (Datenbank)</strong>
                </p>
                <p>
                  Die Server befinden sich in Deutschland. Hierbei handelt es sich um professionelle Hosting-Dienstleister, die hohe Sicherheitsstandards erfüllen.
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Section 4 */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Cookie className="w-6 h-6 text-primary" />
                <h3 className="text-xl font-semibold">4. Cookies</h3>
              </div>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Unsere Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Webbrowser auf Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen.
                </p>
                <p>
                  Einige Cookies sind "Session-Cookies". Solche Cookies werden nach Ende Ihrer Browser-Sitzung von selbst gelöscht. Hingegen bleiben andere Cookies auf Ihrem Endgerät bestehen, bis Sie diese selbst löschen.
                </p>
                <p>
                  Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und einzeln über deren Annahme entscheiden oder die Annahme von Cookies für bestimmte Fälle oder generell ausschließen.
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Section 5 */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">5. Google OAuth</h3>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Wir bieten Ihnen die Möglichkeit, sich über Google OAuth bei unserem Dienst anzumelden. Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.
                </p>
                <p>
                  Wenn Sie sich über Google OAuth anmelden, erhalten wir von Google folgende Informationen: Name, E-Mail-Adresse und Profilbild. Diese Daten verwenden wir ausschließlich zur Verwaltung Ihres Benutzerkontos.
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Section 6 - Amazon Affiliate */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">6. Amazon Partnerprogramm</h3>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Wir sind Teilnehmer des Amazon EU-Partnerprogramms. Wenn Sie auf unserer Plattform Geschenkwünsche mit Amazon-Links eintragen, werden diese Links automatisch in Affiliate-Links umgewandelt.
                </p>
                <p>
                  Bei einem Kauf über diese Links erhalten wir eine kleine Provision von Amazon, ohne dass für Sie zusätzliche Kosten entstehen. Die Einnahmen helfen uns, diesen Dienst weiterzuentwickeln und zu betreiben.
                </p>
                <p>
                  Amazon ist eine Marke der Amazon.com, Inc. oder seiner verbundenen Unternehmen.
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Section 7 */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">7. Ihre Rechte</h3>
              <div className="text-muted-foreground space-y-3">
                <p>Sie haben jederzeit das Recht:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten</li>
                  <li>Die Berichtigung unrichtiger personenbezogener Daten zu verlangen</li>
                  <li>Die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen</li>
                  <li>Die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen</li>
                  <li>Der Verarbeitung Ihrer personenbezogenen Daten zu widersprechen</li>
                  <li>Ihre Daten in einem strukturierten Format zu erhalten (Datenübertragbarkeit)</li>
                </ul>
                <p>
                  Wenn Sie Fragen zum Datenschutz haben oder Ihre Rechte ausüben möchten, wenden Sie sich bitte an: <a href="mailto:info@mokiplaner.com" className="text-primary hover:underline">info@mokiplaner.com</a>
                </p>
              </div>
            </div>
          </div>

          {/* Back Button */}
          <div className="text-center">
            <Link to="/">
              <Button variant="outline" className="rounded-full">
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
